/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, as table-internals/use-row-keyboard-navigation.ts does. */
import * as React from 'react';

type Folding = { expandedByDefault: boolean; flipped: ReadonlySet<string> };

/** Adjacent rows by group id (none when a sort leaves a group in more than one run), which groups are open, and toggles for one or all; a new `defaultExpanded` drops every individual toggle. */
export function useRowGroups<TData>(
  rows: readonly TData[],
  getRowGroupId: ((row: TData) => string | null) | undefined,
  defaultExpanded: boolean,
): {
  hasGroups: boolean;
  /** Each group counted once with every ungrouped row: what a server paging by group counts. */
  pagedCount: number;
  allExpanded: boolean;
  groupIdOf: (index: number) => string | null;
  startsGroup: (index: number) => boolean;
  endsGroup: (index: number) => boolean;
  rowsOf: (groupId: string) => TData[];
  isCollapsed: (groupId: string) => boolean;
  toggle: (groupId: string) => void;
  setAllExpanded: (expanded: boolean) => void;
} {
  const [folding, setFolding] = React.useState<Folding>(() => ({
    expandedByDefault: defaultExpanded,
    flipped: new Set(),
  }));
  const [seenDefault, setSeenDefault] = React.useState(defaultExpanded);
  if (seenDefault !== defaultExpanded) {
    setSeenDefault(defaultExpanded);
    setFolding({ expandedByDefault: defaultExpanded, flipped: new Set() });
  }
  const groupIds = React.useMemo(() => {
    const ids = rows.map((row) => getRowGroupId?.(row) ?? null);
    const opened = new Set<string>();
    const interleaved = ids.some((groupId, index) => {
      if (groupId === null || ids[index - 1] === groupId) return false;
      if (opened.has(groupId)) return true;
      opened.add(groupId);
      return false;
    });
    // A sort that splits a group into runs would give each run a header counting the other's rows, so grouping stands down.
    return interleaved ? ids.map(() => null) : ids;
  }, [rows, getRowGroupId]);
  const rowsByGroupId = React.useMemo(() => {
    const grouped = new Map<string, TData[]>();
    rows.forEach((row, index) => {
      const groupId = groupIds[index];
      if (groupId === null || groupId === undefined) return;
      grouped.set(groupId, [...(grouped.get(groupId) ?? []), row]);
    });
    return grouped;
  }, [rows, groupIds]);
  const toggle = React.useCallback((groupId: string) => {
    setFolding((current) => {
      const flipped = new Set(current.flipped);
      if (flipped.has(groupId)) flipped.delete(groupId);
      else flipped.add(groupId);
      return { ...current, flipped };
    });
  }, []);
  const setAllExpanded = React.useCallback((expanded: boolean) => {
    setFolding({ expandedByDefault: expanded, flipped: new Set() });
  }, []);
  const isCollapsed = (groupId: string) =>
    folding.expandedByDefault === folding.flipped.has(groupId);
  return {
    hasGroups: rowsByGroupId.size > 0,
    pagedCount: groupIds.filter(
      (groupId, index) => groupId === null || groupIds[index - 1] !== groupId,
    ).length,
    allExpanded: [...rowsByGroupId.keys()].every((groupId) => !isCollapsed(groupId)),
    groupIdOf: (index) => groupIds[index] ?? null,
    startsGroup: (index) => {
      const groupId = groupIds[index];
      return groupId !== null && groupId !== undefined && groupIds[index - 1] !== groupId;
    },
    endsGroup: (index) => {
      const groupId = groupIds[index];
      return groupId !== null && groupId !== undefined && groupIds[index + 1] !== groupId;
    },
    rowsOf: (groupId) => rowsByGroupId.get(groupId) ?? [],
    isCollapsed,
    toggle,
    setAllExpanded,
  };
}
